import React, { useState, useEffect } from 'react';
import { Trophy, Crown, Sparkles, Star, Gift, Send, Medal } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { UserAvatar, RankBadge } from '../components/RankBadge';
import VipBadge from '../components/VipBadge';

const MEDAL_COLORS = ['#FBBF24', '#E5E7EB', '#FB923C']; // gold, silver, bronze

export default function Leaderboard() {
  const [topGifters, setTopGifters] = useState([]);
  const [mostGifted, setMostGifted] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/leaderboard/top-gifters').then(r => r.data).catch(() => []),
      api.get('/leaderboard/most-gifted').then(r => r.data).catch(() => []),
    ]).then(([g, m]) => { setTopGifters(g); setMostGifted(m); setLoading(false); });
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-2xl md:text-3xl flex items-center gap-2"><Trophy className="text-amber-400" />Leaderboards</h1>
        <p className="text-white/60 text-sm mt-1">Top travelers ranked by cosmic generosity.</p>
      </div>

      {/* Featured podium */}
      <Card className="bg-gradient-to-br from-amber-500/15 via-fuchsia-500/10 to-cyan-500/10 border-amber-500/20 overflow-hidden">
        <CardContent className="p-5 text-center">
          <Crown className="mx-auto text-amber-300" size={28} />
          <div className="font-display font-semibold text-lg mt-2">Generosity Rules the Cosmos</div>
          <div className="text-xs text-white/60 mt-1">Send thoughtful gifts to climb the Top Gifters board. Receive gifts from friends to appear on Most Loved.</div>
        </CardContent>
      </Card>

      <Tabs defaultValue="gifters">
        <TabsList className="bg-[#161622] border border-white/10">
          <TabsTrigger value="gifters" className="data-[state=active]:bg-white/10"><Send size={14} className="mr-1.5" />Top Gifters</TabsTrigger>
          <TabsTrigger value="gifted" className="data-[state=active]:bg-white/10"><Gift size={14} className="mr-1.5" />Most Loved</TabsTrigger>
        </TabsList>

        <TabsContent value="gifters" className="mt-4">
          <LeaderList rows={topGifters} loading={loading} metricLabel="Total spent" emptyText="No one has sent gifts yet \u2014 be the first!" pick={r => ({ value: r.total_spent, count: r.gifts_sent, countLabel: 'gifts sent' })} />
        </TabsContent>
        <TabsContent value="gifted" className="mt-4">
          <LeaderList rows={mostGifted} loading={loading} metricLabel="Stars received" emptyText="No gifts received yet." pick={r => ({ value: r.total_received, count: r.gifts_received, countLabel: 'kind messages' })} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LeaderList({ rows, loading, metricLabel, emptyText, pick }) {
  if (loading) return <div className="text-center py-10 text-white/40">Loading leaderboard\u2026</div>;
  if (!rows.length) return <div className="text-center py-12 text-white/40">{emptyText}</div>;

  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const m = pick(r);
        const medal = MEDAL_COLORS[i];
        return (
          <Card key={r.user.user_id} className={`bg-[#161622] border-white/10 ${i < 3 ? 'border-amber-400/30' : ''}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold text-lg flex-shrink-0" style={{ background: medal ? medal + '22' : 'rgba(255,255,255,0.04)', color: medal || '#A8A8B3', border: `1px solid ${medal || 'rgba(255,255,255,0.1)'}55` }}>
                {i < 3 ? <Medal size={18} /> : <span className="text-sm">#{i + 1}</span>}
              </div>
              <UserAvatar user={r.user} size={42} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{r.user.displayName}</span>
                  <span className="text-sm">{r.user.flag}</span>
                  <RankBadge rank={r.user.rank} />
                  <VipBadge active={r.user.is_vip} size="sm" />
                </div>
                <div className="text-[11px] text-white/50 mt-0.5">{m.count} {m.countLabel}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-white/40">{metricLabel}</div>
                <div className="font-display font-bold text-amber-300 flex items-center gap-1 justify-end"><Star size={12} fill="currentColor" />{m.value.toLocaleString()}</div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
